import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Briefcase } from "lucide-react";
import { Grid } from "@mui/material";

import Card from "../../../Card";
import { UIEmptyState } from "../../../ui";
import { formatMobileNumber } from "../../../../utils/Helpers";

import "../_EventDetailFields.scss";
import "./_EventClientSection.scss";

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <div className="event-detail-field">
      <span className="event-detail-label">{label}</span>
      <span className="event-detail-value">{value || "—"}</span>
    </div>
  </Grid>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Field.defaultProps = {
  value: "",
};

const resolveEventCompanyClient = (event) => {
  const companyClient = event?.company_client;
  if (companyClient && typeof companyClient === "object") {
    return companyClient;
  }

  if (Array.isArray(event?.clients) && event.clients.length > 0) {
    const legacy = event.clients[0];
    return {
      client_id: legacy?.client_id ?? legacy?.admin?.id ?? null,
      name: legacy?.admin?.name ?? legacy?.name ?? "",
      email: legacy?.admin?.email ?? legacy?.email ?? "",
      phone_number: legacy?.phone_number ?? legacy?.admin?.phone_number ?? "",
      country: legacy?.country ?? legacy?.admin?.country ?? null,
    };
  }

  return null;
};

const hasAssignedEventClient = (companyClient) => {
  if (!companyClient || typeof companyClient !== "object") return false;
  if (companyClient.client_id != null && companyClient.client_id !== "") {
    return true;
  }
  return Boolean(String(companyClient.name ?? "").trim());
};

const EventClientSection = ({ event, labels }) => {
  const companyClient = useMemo(() => resolveEventCompanyClient(event), [event]);
  const hasAssignedClient = hasAssignedEventClient(companyClient);

  const phone = formatMobileNumber(
    companyClient?.phone_number,
    companyClient?.country?.dial_code,
  );

  return (
    <Card
      title={
        <span className="event-client-section__title">
          <Briefcase size={20} strokeWidth={1.75} aria-hidden />
          {labels.client}
        </span>
      }
      classNames={{ body: "event-client-section__body" }}
    >
      {hasAssignedClient ? (
        <Grid container spacing={3}>
          <Field label={labels.clientName} value={companyClient.name} />
          <Field label={labels.clientEmail} value={companyClient.email} />
          <Field label={labels.clientPhone} value={phone} />
        </Grid>
      ) : (
        <UIEmptyState
          title={labels.noClientAssigned}
          description={labels.noClientAssignedDescription}
          classNames={{
            root: "ui-empty-state--embedded event-client-section__empty",
            title: "event-client-section__empty-title text-label-md",
            description:
              "event-client-section__empty-description text-body-xs",
          }}
        />
      )}
    </Card>
  );
};

EventClientSection.propTypes = {
  event: PropTypes.object,
  labels: PropTypes.shape({
    client: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    clientEmail: PropTypes.string.isRequired,
    clientPhone: PropTypes.string.isRequired,
    noClientAssigned: PropTypes.string.isRequired,
    noClientAssignedDescription: PropTypes.string.isRequired,
  }).isRequired,
};

EventClientSection.defaultProps = {
  event: null,
};

export default EventClientSection;
